import { useLocation, useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetPlanetaryTransitAspectQuery } from '@/store/api/planetaryTransitAspectApi';
import Loader from '@/components/Loader';
import { safeRender } from '@/utils/helper.utils';
import { groupBySign } from '@/utils/groupBySign';

export default function PlanetaryTransitAspect() {
  const [params] = useSearchParams();
  const { state } = useLocation();
  const user = state?.user;
  const userId = params.get('userId');
  const rashi = user?.BirthDetail?.astroData?.sign;

  const { data, isLoading, isError } = useGetPlanetaryTransitAspectQuery(
    userId
      ? {
          userId,
          rashi,
        }
      : skipToken
  );

  const tableData: any[] = Array.isArray(data) ? data : (data?.transitAspect ?? []);

  if (isError) {
    return <p className="p-6 text-center text-red-500">Failed to load Transit Aspect data</p>;
  }

  const groupedData = groupBySign(tableData);

  return (
    <div className="">
      <h3 className="font-32 font-semibold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] font-spectral heading-uppercase">
        ASPECTS OF TRANSITING PLANETS ({groupedData?.[0]?.sign})
      </h3>

      <div className="overflow-x-auto">
        {isLoading ? (
          <Loader />
        ) : (
          <table className="min-w-full border-collapse border border-black font-18 whitespace-nowrap">
            <thead className="bg-[#001B6B] text-white font-spectral uppercase">
              <tr>
                <th className="border  px-3 py-2 min-w-[185px]">Natal Sign</th>
                <th className="border  px-3 py-2 min-w-[170px]">Natal Planet(s)</th>
                <th className="border  px-3 py-2 min-w-[110px]">
                  House No <br /> from <br /> Rashi / Moon
                </th>
                <th className="border  px-3 py-2 min-w-[170px]">Transiting Planet(s)</th>
                <th className="border  px-3 py-2">Aspects</th>
                <th className="border px-3 py-2 min-w-[190px]">Signs Aspected</th>
                <th className="border px-3 py-2">
                  Houses <br /> Aspected <br /> from Rashi / Moon
                </th>
                <th className="border  px-3 py-2 min-w-[320px]">Planets Aspected</th>
              </tr>
            </thead>

            <tbody>
              {groupedData.map((group, gi) =>
                group.rows.map((row: any, ri: number) => (
                  <tr
                    key={`${gi}-${ri}`}
                    className={
                      gi % 2 === 0 ? 'bg-white' : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                    }
                  >
                    {ri === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="border border-black px-3 py-2 align-middle"
                      >
                        {group.sign}
                      </td>
                    )}

                    {ri === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="border border-black px-3 py-2 text-center align-middle"
                      >
                        {Array.isArray(row.natalPlanets) && row.natalPlanets.length > 0
                          ? row.natalPlanets.map((p: string, idx: number) => (
                              <div key={idx}>{safeRender(p)}</div>
                            ))
                          : '-'}
                      </td>
                    )}

                    {ri === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="border border-black px-3 py-2 text-center align-middle"
                      >
                        {row.house ?? '-'}
                      </td>
                    )}

                    <td className="border border-black px-3 py-2 text-center">
                      {Array.isArray(row.planet) && row.planet.length > 0
                        ? row.planet.map((p: string, idx: number) => (
                            <div key={idx}>{safeRender(p)}</div>
                          ))
                        : (row.planet ?? '-')}
                    </td>

                    <td className="border border-black px-3 py-2 text-center">
                      {Array.isArray(row.aspect) && row.aspect.length > 0
                        ? row.aspect.map((a: string, idx: number) => (
                            <div key={idx}>{safeRender(a)}</div>
                          ))
                        : '-'}
                    </td>

                    <td className="border border-black px-3 py-2 text-center">
                      {Array.isArray(row.aspectSigns) && row.aspectSigns.length > 0
                        ? row.aspectSigns.map((s: string, idx: number) => (
                            <div key={idx}>{safeRender(s)}</div>
                          ))
                        : '-'}
                    </td>

                    <td className="border border-black px-3 py-2 text-center">
                      {Array.isArray(row.houseAspect) && row.houseAspect.length > 0
                        ? row.houseAspect.map((h: string, idx: number) => (
                            <div key={idx}>{safeRender(h)}</div>
                          ))
                        : '-'}
                    </td>

                    <td className="border border-black px-3 py-2 text-center">
                      {Array.isArray(row.planetAspects) &&
                      row.planetAspects.filter(
                        (p: any) =>
                          p !== null &&
                          p !== undefined &&
                          p !== '' &&
                          !(Array.isArray(p) && p.length === 0)
                      ).length > 0
                        ? row.planetAspects
                            .filter(
                              (p: any) =>
                                p !== null &&
                                p !== undefined &&
                                p !== '' &&
                                !(Array.isArray(p) && p.length === 0)
                            )
                            .map((p: any, idx: number) => <div key={idx}>{safeRender(p)}</div>)
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
